"use client";

import React, { forwardRef, useLayoutEffect, useRef } from "react";
import { useTextField } from "react-aria";

import { useTheme } from "../../ThemeProvider";
import { csx } from "../../../utils";
import "./Input.css";
import { copyTextToClipboard } from "../../../utils/copyTextToClipboard";

export interface BaseInputProps {
  /**
   * onChange handler for the input
   */
  onChange?: (value: string) => void;

  /** Whether the input is disabled. */
  isDisabled?: boolean;

  /** Description text shown above the input. */
  description?: React.ReactNode | string;

  /**
   * The position of the description text.
   * @default "top"
   */
  descriptionPosition?: "top" | "bottom";

  /** Error state that changes the input's visual style and displays an error message. */
  error?: React.ReactNode | string;

  /**
   * Label for the input element.
   * @note When a label is provided, the input will have extra padding, and the label will float above the text input when focused or filled.
   */
  label?: string;

  /** Test ID for the component. */
  "data-testid"?: string;

  /** Content to display before the input. Typically used for icons. */
  prefix?: React.ReactNode;

  /** Content to display after the input. Typically used for icons. */
  suffix?: React.ReactNode;

  /**
   * Should the browser's autocomplete be enabled?
   * @external https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  autoComplete?: boolean;

  /**
   * Should the input be autofocused?
   * @external https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autofocus
   */
  autoFocus?: boolean;

  /**
   * The name attribute of the input element.
   * @external https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/name
   */
  name: string;

  /**
   * The placeholder text to display when the input is empty.
   * @note label takes precedence over placeholder, if both are provided.
   * @external https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/placeholder
   */
  placeholder?: string;

  /**
   * The type attribute of the input element.
   * @external https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/type
   */
  type?: string;

  /**
   * The value of the input.
   * @external https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#value
   */
  value?: string;
}

/**
 * A controlled and customizable input component with support for labels, prefixes, suffixes, and error states.
 */
const Input = forwardRef<HTMLInputElement, BaseInputProps>(
  (
    {
      prefix,
      suffix,
      description,
      descriptionPosition = "top",
      isDisabled,
      error,
      onChange,
      label,
      name,
      placeholder,
      type = "text",
      value,
      autoFocus,
      autoComplete,
      "data-testid": testId,
    },
    forwardedRef: React.RefObject<HTMLInputElement>
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const prefixRef = useRef<HTMLDivElement>(null);
    const suffixRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const { className } = useTheme();
    const ref = forwardedRef || inputRef;
    const { labelProps, inputProps, descriptionProps, errorMessageProps } =
      useTextField(
        {
          label,
          name,
          type,
          autoFocus,
          autoComplete: autoComplete ? "on" : "off",
          "aria-label": label || name,
          "aria-describedby": error ? `${name}-input-error` : `${name}-input`,
          errorMessage: error,
          isDisabled,
          placeholder: label ? undefined : placeholder,
          validationState: error ? "invalid" : "valid",
          value,
          onChange,
          onCopy: async () => {
            await copyTextToClipboard(inputRef.current?.value);
          },
        },
        ref
      );

    // Control dynamic prefix and suffix widths
    useLayoutEffect(() => {
      if (!containerRef.current) return;

      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth || 200;
        containerRef.current.style.setProperty(
          "--container-width",
          `${containerWidth}px`
        );
      }

      if (prefixRef.current) {
        const prefixWidth = prefixRef.current.offsetWidth || 32;
        containerRef.current.style.setProperty(
          "--prefix-width",
          `${prefixWidth}px`
        );
      }

      if (suffixRef.current) {
        const suffixWidth = suffixRef.current.offsetWidth || 32;
        containerRef.current.style.setProperty(
          "--suffix-width",
          `${suffixWidth}px`
        );
      }
    }, [prefix, suffix]);

    return (
      <div className="proton-Input__container" ref={containerRef}>
        {description && descriptionPosition === "top" && (
          <div {...descriptionProps} className="proton-Input__text">
            {description}
          </div>
        )}
        <div className="proton-Input__container-inner">
          {prefix && (
            <div
              ref={prefixRef}
              className={csx(
                "proton-Input__descriptor",
                "proton-Input__prefix"
              )}
            >
              {prefix}
            </div>
          )}

          <input
            {...inputProps}
            data-testid={testId}
            id={`${name}-input`}
            aria-invalid={Boolean(error)}
            ref={ref}
            className={csx(
              "proton-Input",
              error && "proton-Input--error",
              label && "proton-Input__label-top",
              className
            )}
          />

          <label
            {...labelProps}
            className={csx(
              "proton-Input__label",
              value && "proton-Input__label--filled"
            )}
          >
            {label}
          </label>

          {suffix && (
            <div
              ref={suffixRef}
              className={csx(
                "proton-Input__descriptor",
                "proton-Input__suffix"
              )}
            >
              {suffix}
            </div>
          )}
        </div>

        {error ? (
          <div
            {...errorMessageProps}
            role="alert"
            className={csx("proton-Input__error", "proton-Input__text")}
            id={`${name}-error`}
          >
            {error}
          </div>
        ) : description && descriptionPosition === "bottom" ? (
          <div {...descriptionProps} className="proton-Input__text">
            {description}
          </div>
        ) : null}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
